import { PublicOutlined as PublicOutlinedIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import PermissionButton from 'components/buttons/PermissionButton';
import { useModal } from 'components/modals/useModal';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useLazyGetCountryManagementWithFiltersQuery,
    useUpdateCountryManagementMutation,
} from 'store/api/country-management';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { CountryManagementListItem, CountryManagementUpdateDto } from 'types/country-management';
import { t } from 'utils/getTranslations';
import { getLinks } from '../matters/util';
import CountryManagementEditModal from './CountryManagementEditModal';

const CountryManagementTable: React.FC = () => {
    const [fetchData, { data, isFetching }] = useLazyGetCountryManagementWithFiltersQuery();
    const [updateCountry] = useUpdateCountryManagementMutation();
    const { openModal, closeModal } = useModal();

    const handleSave = useCallback(
        async (countryCode: string, dto: CountryManagementUpdateDto) => {
            try {
                await updateCountry({ countryCode, ...dto }).unwrap();
                closeModal();
            } catch (e) {
                console.error('Error updating country management:', e);
            }
        },
        [updateCountry, closeModal]
    );

    const openEditModal = useCallback(
        (country: CountryManagementListItem) => {
            openModal(
                <CountryManagementEditModal country={country} onClose={closeModal} onSave={handleSave} />,
                closeModal
            );
        },
        [openModal, closeModal, handleSave]
    );

    // Weights are NOT measured against a live render: /country-management redirects to /home without
    // READ.BACK_OFFICE_COUNTRY_MANAGEMENT, so no baseline was reachable. The content here is short
    // and uniform apart from the comma-joined translations list, which is the one column that gets a
    // boost; the two tooltip-bearing headers set their own minimum from their one-line width.
    const columns = useMemo<DataTableColumnDef<CountryManagementListItem>[]>(
        () => [
            {
                header: t('country-management.country-eng'),
                id: 'nameEng',
                accessorKey: 'nameEng',
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                    // The localized English name is not a DB column; sort by the country code instead
                    // (shown in the cell). Ordering is by code, not the displayed English label.
                    sortAccessor: 'code',
                },
                cell: ({ row }) => {
                    const { nameEng, countryCode } = row.original;
                    return (
                        <div className="font-semibold whitespace-nowrap">
                            {nameEng} ({countryCode})
                        </div>
                    );
                },
            },
            {
                header: t('country-management.country-translations'),
                id: 'translations',
                enableSorting: false,
                meta: {
                    // The only long-text column (every localized country name, comma-joined).
                    widthWeight: 1.6,
                    minPx: 180,
                },
                cell: ({ row }) => {
                    const { translations } = row.original;
                    if (!translations || translations.length === 0) return '-';
                    return translations.map((tr) => tr.value).join(', ');
                },
            },
            {
                header: t('country-management.high-risk-jurisdiction'),
                id: 'isHighRiskJurisdiction',
                accessorKey: 'isHighRiskJurisdiction',
                meta: {
                    widthWeight: 1,
                    minPx: 165,
                },
                cell: ({ getValue }) =>
                    getValue<boolean>() ? <FormattedMessage id="yes" /> : <FormattedMessage id="no" />,
            },
            {
                header: () => (
                    <div className="flex items-center gap-1">
                        {t('country-management.market-ambassadors')}
                        <InfoTooltip text="country-management.tooltips.market-ambassadors" />
                    </div>
                ),
                id: 'ambassadors',
                enableSorting: false,
                meta: {
                    widthWeight: 1.3,
                    minPx: 190,
                },
                cell: ({ row }) => {
                    const { ambassadors } = row.original;
                    if (!ambassadors || ambassadors.length === 0) return '-';
                    return getLinks(
                        'employee',
                        ambassadors.map((a) => ({
                            ...a,
                            suffix: a.isActive ? undefined : `(${t('inactive')})`,
                        })),
                        { target: '_blank' }
                    );
                },
            },
            {
                header: () => (
                    <div className="flex items-center gap-1">
                        {t('country-management.strategic-market')}
                        <InfoTooltip text="country-management.tooltips.strategic-market" nested />
                    </div>
                ),
                id: 'isStrategicMarket',
                accessorKey: 'isStrategicMarket',
                meta: {
                    widthWeight: 1,
                    minPx: 165,
                },
                cell: ({ getValue }) =>
                    getValue<boolean>() ? <FormattedMessage id="yes" /> : <FormattedMessage id="no" />,
            },
            {
                header: '',
                id: 'actions',
                enableSorting: false,
                meta: {
                    // 37px "Edit" button + the managed cell's 32px horizontal padding.
                    fixedPx: 72,
                },
                cell: ({ row }) => (
                    <div className="flex justify-end">
                        <PermissionButton
                            permissions={['UPDATE.BACK_OFFICE_COUNTRY_MANAGEMENT']}
                            variant="outlined"
                            size="extraSmall"
                            onClick={() => openEditModal(row.original)}
                        >
                            <FormattedMessage id="edit" />
                        </PermissionButton>
                    </div>
                ),
            },
        ],
        [openEditModal]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [
                {
                    type: 'dynamicmultiselect',
                    label: t('country-management.filter.market-ambassadors'),
                    selector: 'code',
                    comperator: '=hasambassador=',
                    tooltip: 'country-management.tooltips.filter-market-ambassadors',
                    group: 'country-management.countries',
                    paginated: {
                        fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'checkboxgroup',
                    selector: 'isHighRiskJurisdiction',
                    group: 'country-management.countries',
                    filters: [
                        {
                            label: t('country-management.filter.high-risk-only'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'isStrategicMarket',
                    group: 'country-management.countries',
                    filters: [
                        {
                            label: t('country-management.filter.strategic-only'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'code',
                    comperator: '=hasinactiveambassador=',
                    group: 'country-management.countries',
                    filters: [
                        {
                            label: t('country-management.filter.inactive-ambassador'),
                            argument: 'true',
                            tooltip: 'country-management.tooltips.inactive-ambassador',
                        },
                    ],
                },
            ],
            searchBarSelectors: [{ selector: 'texts.value' }, { selector: 'code' }],
        }),
        []
    );

    return (
        <ListTableWithFilters<CountryManagementListItem>
            columnConfigId="country-management"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="country-management.entries"
            singularTextId="country-management.entry"
            noEntriesId="country-management.no-countries-found"
            noEntriesBodyId="country-management.empty.body"
            noEntriesIcon={PublicOutlinedIcon}
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default CountryManagementTable;
